import { supabase } from '@/lib/supabase';

export const INSTRUCTIONS_SYNC_EVENT = '__jphph_instructions_sync__';

export interface ManagementInstructionRecord {
  id: string;
  project_id: string;
  stage_id: string | null;
  from_user_id: string;
  to_user_id: string | null;
  instruction_title: string;
  body: string;
  priority: string;
  status: string;
  requires_response: boolean;
  due_at: string | null;
  created_at: string;
  read_at: string | null;
  acknowledged_at: string | null;
  completed_at: string | null;
  linked_schedule_item_id: string | null;
  linked_checklist_item_id: string | null;
  linked_photo_id: string | null;
  management_note: string | null;
  instruction_type: string | null;
  cost_impact: string | null;
  schedule_impact: string | null;
  requires_client_approval: boolean;
  change_order_reason: string | null;
  current_finish_description: string | null;
  new_finish_description: string | null;
}

export function emitInstructionsSync(projectId?: string): void {
  window.dispatchEvent(
    new CustomEvent(INSTRUCTIONS_SYNC_EVENT, { detail: { projectId } })
  );
}

/** Internal-only notes (never sent to subcontractor) can be permanently deleted. */
export function canHardDeleteInstruction(ins: {
  to_user_id: string | null;
  status: string;
}): boolean {
  return !ins.to_user_id && ins.status === 'closed';
}

export async function archiveInstruction(
  id: string,
  projectId?: string
): Promise<{ error?: string }> {
  const { error } = await supabase
    .from('jphph_instructions')
    .update({ status: 'closed' })
    .eq('id', id);
  if (error) return { error: error.message };
  emitInstructionsSync(projectId);
  return {};
}

export async function deleteInstruction(ins: {
  id: string;
  project_id: string;
  to_user_id: string | null;
  status: string;
}): Promise<{ error?: string; archived?: boolean }> {
  if (canHardDeleteInstruction(ins)) {
    await supabase
      .from('jphph_project_files')
      .update({ instruction_id: null })
      .eq('instruction_id', ins.id);
    const { error } = await supabase
      .from('jphph_instructions')
      .delete()
      .eq('id', ins.id);
    if (error) return { error: error.message };
    emitInstructionsSync(ins.project_id);
    return {};
  }

  const result = await archiveInstruction(ins.id, ins.project_id);
  if (result.error) return result;
  return { archived: true };
}

export async function unlinkInstructionAttachment(fileId: string): Promise<void> {
  await supabase
    .from('jphph_project_files')
    .update({ instruction_id: null })
    .eq('id', fileId);
}
